import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useUI } from 'hooks/useUI';
import { useBusiness } from 'hooks/useBusiness';
import ControlHeader from 'components/activity-display/control-header/ControlHeader';
import getHeaderElementSetup from 'components/activity-display/control-header/getHeaderElementSetup';
import { HeaderThemeOption } from 'interfaces/HeaderThemeSetups';
import { Alert } from '@mui/material';

interface HeaderDetailsProps {
	headerElement: HTMLElement | Element | null;
	headerIsSticky: boolean;
	hasHeaderElement: boolean;
}

function ControlHeaderContainer() {
	const [cartBarHeight, setCartBarHeight] = useState(0);
	const [cartBarMarkerYPosition, setCartBarMarkerYPosition] = useState<number>(0);
	const [extraYThreshold, setExtraYThreshold] = useState(0);
	const { setCartBarWrapperElement } = useActivityShopUI();
	const { windowWidth, isEmbedded, cmsSlug, cmsThemeSlug, cmsThemeVersion, headerName, headerType, headerIsSticky, mobileHeaderName, mobileHeaderType, mobileHeaderIsSticky, mobileHeaderUntilWidth } = useUI();
	const [wpAdminBarIsSticky, setWpAdminBarIsSticky] = useState(windowWidth > 600);
	const [headerError, setHeaderError] = useState<string|null>(null);

	const markerRef = useRef<HTMLDivElement | null>(null);
	const cartBarWrapperRef = useRef<HTMLDivElement | null>(null);

	//ref with useEffect, to let handleResize and handleScroll access it later
	const windowWidthRef = useRef(windowWidth);
	const wpAdminBarIsStickyRef = useRef(wpAdminBarIsSticky);

	useEffect(() => { windowWidthRef.current = windowWidth; }, [windowWidth]);
	useEffect(() => { wpAdminBarIsStickyRef.current = wpAdminBarIsSticky; }, [wpAdminBarIsSticky]);

	const updateWpAdminBarIsSticky = (value: boolean) => {
        setWpAdminBarIsSticky(value);
        wpAdminBarIsStickyRef.current = value;
    };

	function getHeaderDetailsFromOptions(headerElementOptions: HeaderThemeOption[], currentWindowWidth: number, isCustomHeader: boolean = false, musthaveHeaderElement: boolean): HeaderDetailsProps {
		let element;

		for (const option of headerElementOptions) {
			if (!option.untilWidth || currentWindowWidth < option.untilWidth) { //Found a config option for current window size
				switch (option.type) {
					case 'id':
						element = document.getElementById(option.name);
						break;
					case 'class':
						element = document.querySelector(`.${option.name}`);
						break;
					default:
						throw new Error(`Invalid header element type "${option.type}" with name "${option.name}"`);
				}

				if (!!element) { //Found element
					if (headerError !== null) { //Had error
						setHeaderError(null); //Remove error
					}

					return {
						headerElement: element,
						headerIsSticky: option.isSticky,
						hasHeaderElement: !!element
					};
				}
			}
		}

		if (musthaveHeaderElement) {
			if (headerError === null) {
				setHeaderError('Header element not found. Please specify a valid header HTML element for attaching the cart bar, or contact TourTask for support.');
				console.log('Did not find header element for window width ', currentWindowWidth, ' with options:', headerElementOptions);
			}
		}

		//No header element found
		return {
			headerElement: null,
			headerIsSticky: false,
			hasHeaderElement: false
		};
	}

	function getHeaderDetails(): HeaderDetailsProps {
		const currentWindowWidth = windowWidthRef.current;
		const currentWpAdminBarIsStickyRef = wpAdminBarIsStickyRef.current;

		const newWpAdminBarIsSticky = (currentWindowWidth > 600);

		if (currentWpAdminBarIsStickyRef !== newWpAdminBarIsSticky) {
			updateWpAdminBarIsSticky(newWpAdminBarIsSticky);
		}
		
		const configHeaderElement = {
			name: headerName,
			type: headerType,
			isSticky: headerIsSticky,
		};

		if (isEmbedded) {
			const musthaveHeaderElement = (configHeaderElement && !!configHeaderElement.name) || !!cmsSlug;

			let headerElementSetup: HeaderThemeOption[] = [];
			let isCustomHeader;

			if (configHeaderElement && configHeaderElement.name && headerName && headerType) {
				isCustomHeader = true;

				if (mobileHeaderName && mobileHeaderType) {
					headerElementSetup.push({
						name: mobileHeaderName,
						type: mobileHeaderType,
						isSticky: mobileHeaderIsSticky,
						untilWidth: mobileHeaderUntilWidth,
					});
				}

				headerElementSetup.push({
					name: headerName,
					type: headerType,
					isSticky: headerIsSticky,
				});
			} else { //No custom element found - fallback to standard headers
				// Fallback search for standard headers if no custom configuration is found or the element is not present
				isCustomHeader = false;
				headerElementSetup = getHeaderElementSetup(cmsSlug, cmsThemeSlug, cmsThemeVersion);
			}
			
			return getHeaderDetailsFromOptions(headerElementSetup, currentWindowWidth, isCustomHeader, musthaveHeaderElement);
		}
		
		return {
			headerElement: null,
			headerIsSticky: false,
			hasHeaderElement: false,
		};
	};

	const [headerDetails, setHeaderDetails] = useState<HeaderDetailsProps>(getHeaderDetails());

	const headerDetailsRef = useRef(headerDetails);
	useEffect(() => { headerDetailsRef.current = headerDetails; }, [headerDetails]);

	const headerElementsEqual = (obj1: any, obj2: any): boolean => {
		if (obj1 === obj2) return true;

		if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
			return false;
		}

		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		if (keys1.length !== keys2.length) {
			return false;
		}

		for (const key of keys1) {
			if (key === 'headerElement') {
				if (!obj1[key].isEqualNode(obj2[key])) {
					return false;
				}
			} else if (!headerElementsEqual(obj1[key], obj2[key])) {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		const handleResize = () => {
			const newHeaderDetails = getHeaderDetails();
			
			if (newHeaderDetails.headerElement !== null) { //Has a header
				const currentHeaderDetails = headerDetailsRef.current;
	
				if (!headerElementsEqual(currentHeaderDetails, newHeaderDetails)) {
					setHeaderDetails(newHeaderDetails);
				}
			}
		};

		if (isEmbedded) {
			window.addEventListener('resize', handleResize);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		//Setup scroll
		const handleScroll = () => {
			//Check static cart bar Y position
			const cartBarWrapper = document.getElementById('tt-cart-bar-wrapper');
			const cartBarMarker = document.getElementById('tt-cart-bar-marker');
			const cartBarReplacement = document.getElementById('tt-cart-bar-replacement');

			if (cartBarMarker && cartBarWrapper && cartBarReplacement) {
				const newCartBarHeight = cartBarWrapper.offsetHeight;
				if (newCartBarHeight !== cartBarHeight) {
					setCartBarHeight(newCartBarHeight);
				}

				const cartBarMarkerYPositionNew = cartBarMarker.getBoundingClientRect().top + window.scrollY;

				if (cartBarMarkerYPositionNew !== cartBarMarkerYPosition) {
					setCartBarMarkerYPosition(cartBarMarkerYPositionNew);
				}

				let wpAdminBarHeight = 0;

				if (headerDetails && (!headerDetails.headerIsSticky)) {
					const wpAdminBar = document.getElementById('wpadminbar');
					wpAdminBarHeight = wpAdminBar ? wpAdminBar.clientHeight : 0;
	
					if (extraYThreshold !== wpAdminBarHeight) {
						setExtraYThreshold(wpAdminBarHeight);
					}
				}

				const currentWpAdminBarIsStickyRef = wpAdminBarIsStickyRef.current;

				if (
					(headerDetails.headerIsSticky && window.scrollY) ||
					(!headerDetails.headerIsSticky && (window.scrollY >= (cartBarMarkerYPositionNew - (currentWpAdminBarIsStickyRef ? wpAdminBarHeight : 0))))
				) { //Make sticky
				//	console.log('Make sticky', 'window.scrollY', window.scrollY, 'cartBarMarkerYPositionNew', cartBarMarkerYPositionNew, 'wpAdminBarHeight', wpAdminBarHeight);
					cartBarWrapper.classList.add('tt-cart-bar-wrapper-sticky');
					cartBarReplacement.classList.remove('displayNone');
				} else { //No sticky
				//	console.log('No sticky', 'window.scrollY', window.scrollY, 'cartBarMarkerYPositionNew', cartBarMarkerYPositionNew, 'wpAdminBarHeight', wpAdminBarHeight);
					cartBarWrapper.classList.remove('tt-cart-bar-wrapper-sticky');
					cartBarReplacement.classList.add('displayNone');
				}
			}
		};

		window.addEventListener('scroll', handleScroll);
		window.addEventListener('resize', handleScroll);

		if (!markerRef.current && headerDetails.headerElement) {
			if (headerDetails.headerElement && headerDetails.headerElement.parentNode) {
				const cartBarMarker = document.createElement('div');
				cartBarMarker.id = 'tt-cart-bar-marker';
				markerRef.current = cartBarMarker; // Store the created marker in the ref
				const nextSibling = headerDetails.headerElement.nextSibling;
				headerDetails.headerElement.parentNode.insertBefore(cartBarMarker, nextSibling);

				//Update cart bar marker position
				const cartBarMarkerPosition = cartBarMarker.getBoundingClientRect().top + window.scrollY;
				setCartBarMarkerYPosition(cartBarMarkerPosition);
		
				//Create a container for the portal content right after marker
				const portalContainer = document.createElement('div');
				portalContainer.id = 'tt-cart-bar-portal-container';
				headerDetails.headerElement.parentNode.insertBefore(portalContainer, nextSibling);
			}
		}

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
		};
	}, [headerDetails.headerElement, headerDetails.headerIsSticky]);

	useEffect(() => {
		const cartBarReplacement = document.getElementById('tt-cart-bar-replacement');
		if (cartBarReplacement) {
			// Directly set the height of cartBarReplacement when cartBarHeight updates
			cartBarReplacement.style.height = `${cartBarHeight}px`;
		}
	}, [cartBarHeight]); // Depend on cartBarHeight to re-run this effect when it changes
	
	useEffect(() => {
		if (cartBarWrapperRef.current) {
			const newCartBarHeight = cartBarWrapperRef.current.offsetHeight;
			if (newCartBarHeight !== cartBarHeight) {
				setCartBarHeight(newCartBarHeight);
			}
		}
		setCartBarWrapperElement(cartBarWrapperRef.current);
	}, [cartBarWrapperRef]);

	const setCartBarWrapperRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            const newCartBarHeight = node.offsetHeight;
            if (newCartBarHeight !== cartBarHeight) {
                setCartBarHeight(newCartBarHeight);
            }
            setCartBarWrapperElement(node);
        }
        cartBarWrapperRef.current = node;
    }, [cartBarHeight, setCartBarWrapperElement]);

	return(
		<>
			{
				headerDetails.hasHeaderElement ? (
					<ControlHeaderPortal cartBarHeight={cartBarHeight} cartBarMarkerYPosition={cartBarMarkerYPosition} extraYThreshold={wpAdminBarIsSticky ? extraYThreshold : 0} headerIsSticky={headerDetails.headerIsSticky} wpAdminBarIsSticky={wpAdminBarIsSticky} setCartBarWrapperRef={setCartBarWrapperRef} />
				) : (
					<>
						<div id="tt-cart-bar-marker" />
						<div id="tt-cart-bar-replacement" className="displayNone" style={{ height: `${cartBarHeight}px` }} />
						<div id="tt-cart-bar-wrapper" ref={cartBarWrapperRef} style={{ top: `${wpAdminBarIsSticky ? extraYThreshold : 0}px` }}>
							<ControlHeader />
						</div>
					</>
				)
			}
			{
				headerError !== null ? (
					<div style={{ padding: '20px' }}>
						<Alert severity="error">{headerError}</Alert>
					</div>
				) : null
			}
		</>
	);
}

interface ControlHeaderPortalProps {
	cartBarHeight: number;
	cartBarMarkerYPosition: number;
	extraYThreshold: number;
	headerIsSticky: boolean;
	wpAdminBarIsSticky: boolean;
	setCartBarWrapperRef: (node: HTMLDivElement | null) => void;
}

function ControlHeaderPortal({ cartBarHeight, cartBarMarkerYPosition, extraYThreshold, headerIsSticky, wpAdminBarIsSticky, setCartBarWrapperRef }: ControlHeaderPortalProps) {
	const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    const currentElement = document.getElementById('tt-cart-bar-portal-container');
                    if (currentElement && currentElement !== portalElement) {
                        setPortalElement(currentElement);
                        observer.disconnect();
                    }
                }
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });

        // Cleanup observer on component unmount
        return () => {
            observer.disconnect();
        };
    }, [portalElement]);

    return portalElement ? createPortal(
        <>
            <div id="tt-cart-bar-replacement" className="displayNone" style={{ height: `${cartBarHeight}px` }}></div>
            <div id="tt-cart-bar-wrapper" ref={setCartBarWrapperRef} style={{ top: `${(headerIsSticky ? cartBarMarkerYPosition : 0) + ((!headerIsSticky && wpAdminBarIsSticky) ? extraYThreshold : 0)}px` }}>
                <ControlHeader />
            </div>
        </>,
        portalElement
    ) : null;
};

export default ControlHeaderContainer;